// SEO-12 · /resources/questions-to-ask-before-ai-touches-student-records
//
// EDITORIAL GROUNDING (see docs/marketing/RESOURCES_EDITORIAL_PROCESS.md):
//   • Claims registry D9: permissions re-checked per request; answers stay inside the asking
//     school's data; ONE redaction funnel before any model call; a write is PROPOSED and confirmed
//     by a person; health/infirmary/wellbeing/counselling excluded from the surface; usage metered.
//     "Aira can only reach what you can already reach."
//   • Registry S7 (⚠, approved wording): "The default AI router pattern-redacts common identifiers.
//     Name removal depends on the calling flow supplying known values; permissions and source
//     records remain authoritative." NEVER claim universal redaction across every call path.
//   • Registry S8 (⚠): "Current student-AI context excludes the named health, infirmary, wellbeing
//     and counselling sources" — the I-09 firewall; never generalise to every AI integration.
//   • Registry D10: the model never writes or executes SQL — it routes a question to one key in a
//     fixed catalogue, the vetted parameterised query runs tenant-scoped, and narration runs over an
//     aggregate digest with identifying columns dropped.
//   • Registry A3: Parent Aira answers only within the authenticated family's own child scope.
//   • Article #2 posture: name our own weak answers rather than hiding them.
// NO accuracy claims, no "safe AI" guarantee, no certification.

const article = {
  slug: "questions-to-ask-before-ai-touches-student-records",
  title: "What to ask before you let AI near student records",
  seoTitle: "Questions to Ask Before School AI | Vidyapeeth360",
  description:
    "Eight questions to ask any vendor offering AI over student data — and our own answers, including the ones that are boundaries rather than features.",
  topic: "Buying guide",
  topicKey: "buying",
  readingMinutes: 6,
  published: "2026-08-18",
  updated: "2026-08-18",
  standfirst:
    "Assistants are being added to school software faster than schools are being given a way to evaluate them. These are the questions that actually separate a governed assistant from a text box wired to a model.",
  moduleLinks: ["aira-assistant", "mis-reports", "role-and-permission"],
  conversion: {
    to: "/contact",
    label: "Ask us these questions directly",
    note: "We will answer on the record, including where the honest answer is a limitation rather than a feature.",
  },
  sections: [
    {
      id: "why-different",
      heading: "Why school data raises the bar",
      blocks: [
        {
          t: "p",
          text: "A school's database is mostly information about children: names, dates of birth, guardians' phone numbers, marks, attendance, medical notes, and who owes money. Almost every category is one a school would be uncomfortable seeing outside the building.",
        },
        {
          t: "p",
          text: "An assistant is not inherently dangerous with that data. What is dangerous is an assistant whose access is undefined, whose outbound data flow is undocumented, and whose actions are indistinguishable from a staff member's. The questions below are the ones that surface all three, and they can be asked in a demo.",
        },
      ],
    },
    {
      id: "the-questions",
      heading: "Eight questions, in the order they matter",
      blocks: [
        {
          t: "ol",
          items: [
            "**What can it read, and whose permission does it use?** The only good answer is: exactly what the person asking can already read, re-checked at the moment of asking. An assistant with its own service-level access is a permission bypass with a chat interface.",
            "**What leaves the building?** Ask which text reaches the model provider, and what is removed first. Ask to see the boundary in the product, not on a slide.",
            "**Which data is excluded entirely?** Health, infirmary, counselling and wellbeing notes about a child should not be part of an assistant's surface at any permission level. If the vendor has not thought about this, they have not thought about children's data.",
            "**Can it change anything on its own?** A write action must be proposed and confirmed by a person. \"It can update records for you\" is a feature request from somebody who has not yet had to reverse one.",
            "**Which numbers are computed and which are written?** Financial and academic figures should come from the same deterministic code that produces your reports, with the model writing only the sentence around them. If the model produces the number, the number is a guess.",
            "**Does it write queries?** If an assistant generates database queries, its blast radius is your whole database. A safer design routes a question to a fixed catalogue of vetted, parameterised reports.",
            "**Can a parent see another family's child?** For any parent-facing assistant, the answer must be structurally no — enforced by the child scope of the authenticated account, not by prompt instructions.",
            "**What does it cost, and who is metered?** An assistant with unbounded usage is an unbounded bill. Ask how usage is measured, where it is visible, and what happens when a limit is reached.",
          ],
        },
      ],
    },
    {
      id: "wrong-answers",
      heading: "Three answers that should worry you",
      blocks: [
        {
          t: "ul",
          items: [
            "**\"It is completely private, nothing is sent anywhere.\"** Unless the model runs on the school's own infrastructure, something is sent somewhere. The useful answer describes what is sent and what is stripped — not that the question does not apply.",
            "**\"The AI is trained on your school's data.\"** Ask whether that means fine-tuning on your records and, if so, where those records then live and who else's assistant benefits from them. Most of the time the phrase is loose marketing for retrieval, and it should be corrected rather than repeated.",
            "**\"It is accurate.\"** Language models produce fluent wrong answers. The right claim is narrower: the numbers are deterministic, the sources are named, and material answers are verified by the person acting on them.",
          ],
        },
      ],
    },
    {
      id: "our-answers",
      heading: "Our answers, including the uncomfortable ones",
      blocks: [
        {
          t: "p",
          text: "The same questions, answered for Vidyapeeth360 — with the boundaries stated as boundaries.",
        },
        {
          t: "p",
          text: "Aira reads through the same permission checks as the person asking, re-resolved on every request, and stays inside that school's own data. Every model call goes through a single funnel that pattern-redacts common identifiers before the text leaves the process; name removal depends on the calling flow supplying the known values, and permissions and the source records — not the redactor — remain the authoritative boundary. That is a narrower claim than \"everything is anonymised\", and it is the accurate one.",
        },
        {
          t: "p",
          text: "Health, infirmary, wellbeing and counselling sources are excluded from the student-AI surface. Writes are proposed and confirmed by a person; nothing is committed on the assistant's own authority. Numbers stay deterministic — fee, attendance and result figures come from the same code paths that produce the reports, and the assistant writes the explanation around them.",
        },
        {
          t: "p",
          text: "For reporting questions, the model never writes or runs a query: it selects one report from a fixed catalogue, the vetted parameterised query runs scoped to the school, and the narration is generated over an aggregate summary with identifying columns dropped — so the full result, with names, reaches the administrator's browser and not the model. Parent Aira resolves only the children on the asking account. Usage is metered as credits with the consumption visible, and the whole assistant follows plan availability, off until a school enables it.",
        },
        {
          t: "p",
          text: "What we do not claim: that a model is accurate, that redaction is universal across every conceivable call path, or that any of this substitutes for your own review. The design principle is simply that the assistant can only reach what you can already reach — which is why the [access model](/modules/role-and-permission) matters more than the assistant, and why [reports](/modules/mis-reports) and [Aira](/modules/aira-assistant) share one permission story rather than two.",
        },
      ],
    },
  ],
  sources: [],
};

export default article;
